/**
 * AppLoader / AppError — full-page dark-canvas screens.
 *
 * AppLoader: shown while OIDC session resolves or Me query is in-flight.
 * AppError:  shown for 4xx / 5xx conditions — same canvas, error state icon.
 */

// ── Shared canvas ─────────────────────────────────────────────────────────────

const ERROR_CANVAS_ID = "app-error-title";

// ── Loading compass ───────────────────────────────────────────────────────────

function CompassRose() {
  return (
    <div className="relative flex items-center justify-center">
      {/* Outer ring — slow rotation */}
      <svg
        className="absolute size-28 animate-[spin_8s_linear_infinite] opacity-20"
        viewBox="0 0 112 112"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="56" cy="56" r="52" stroke="#047cc7" strokeWidth="1.5" strokeDasharray="6 4" />
        {[0, 90, 180, 270].map((deg) => (
          <line
            key={deg}
            x1="56"
            y1="4"
            x2="56"
            y2="14"
            stroke="#047cc7"
            strokeWidth="2"
            strokeLinecap="round"
            transform={`rotate(${deg} 56 56)`}
          />
        ))}
        {[45, 135, 225, 315].map((deg) => (
          <line
            key={deg}
            x1="56"
            y1="6"
            x2="56"
            y2="12"
            stroke="#047cc7"
            strokeWidth="1.5"
            strokeLinecap="round"
            transform={`rotate(${deg} 56 56)`}
          />
        ))}
      </svg>

      {/* Inner ring — counter-rotation */}
      <svg
        className="absolute size-20 animate-[spin_5s_linear_infinite_reverse] opacity-30"
        viewBox="0 0 80 80"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="40" cy="40" r="36" stroke="#14b8a6" strokeWidth="1" strokeDasharray="3 5" />
      </svg>

      {/* Compass needle — pulse */}
      <svg
        className="relative size-12 animate-[pulse_2s_ease-in-out_infinite]"
        viewBox="0 0 48 48"
        fill="none"
        aria-hidden="true"
      >
        <polygon points="24,4 28,24 24,20 20,24" fill="#047cc7" />
        <polygon points="24,44 28,24 24,28 20,24" fill="#047cc7" opacity="0.3" />
        <circle cx="24" cy="24" r="2.5" fill="#14b8a6" />
      </svg>
    </div>
  );
}

// ── Error icon — broken compass needle ───────────────────────────────────────

const ERROR_CONFIG = {
  "4xx": {
    color: "#f59e0b", // signal amber — "lost at sea"
    ringColor: "#f59e0b",
    label: "Off the chart",
  },
  "5xx": {
    color: "#ef4444", // coral red — "distress"
    ringColor: "#ef4444",
    label: "Rough seas",
  },
} as const;

type ErrorKind = keyof typeof ERROR_CONFIG;

function BrokenCompass({ kind }: { kind: ErrorKind }) {
  const cfg = ERROR_CONFIG[kind];
  return (
    <div className="relative flex items-center justify-center">
      {/* Static outer ring */}
      <svg className="absolute size-28 opacity-20" viewBox="0 0 112 112" fill="none" aria-hidden="true">
        <circle cx="56" cy="56" r="52" stroke={cfg.ringColor} strokeWidth="1.5" strokeDasharray="6 4" />
        {[0, 90, 180, 270].map((deg) => (
          <line
            key={deg}
            x1="56"
            y1="4"
            x2="56"
            y2="14"
            stroke={cfg.ringColor}
            strokeWidth="2"
            strokeLinecap="round"
            transform={`rotate(${deg} 56 56)`}
          />
        ))}
      </svg>

      {/* Tilted / broken needle */}
      <svg
        className="relative size-12 animate-[pulse_3s_ease-in-out_infinite]"
        viewBox="0 0 48 48"
        fill="none"
        aria-hidden="true"
        style={{ transform: "rotate(35deg)" }}
      >
        <polygon points="24,4 28,24 24,20 20,24" fill={cfg.color} />
        <polygon points="24,44 28,24 24,28 20,24" fill={cfg.color} opacity="0.3" />
        <circle cx="24" cy="24" r="2.5" fill={cfg.color} opacity="0.6" />
      </svg>
    </div>
  );
}

import { motion } from "framer-motion";
import { AnchorIcon, CheckCircle2Icon, CompassIcon, MapIcon, Navigation2Icon, WindIcon } from "lucide-react";
import { useEffect, useState } from "react";

// ── Departure checklist ───────────────────────────────────────────────────────

const CHECKLIST = [
  { label: "Setting sail", icon: Navigation2Icon, delay: 0.3 },
  { label: "Checking the charts", icon: MapIcon, delay: 0.7 },
  { label: "Plotting your course", icon: CompassIcon, delay: 1.1 },
  { label: "Reading the wind", icon: WindIcon, delay: 1.5 },
  { label: "All hands ready", icon: AnchorIcon, delay: 1.9 },
];

const CK_EASE: [number, number, number, number] = [0.23, 1, 0.32, 1];

/** Total time: last delay + settle = ~2.2s. Keep in sync with _authenticated min timer. */

function DepartureChecklist() {
  const [checked, setChecked] = useState(0);

  useEffect(() => {
    const timers = CHECKLIST.map((item, i) => setTimeout(() => setChecked(i + 1), item.delay * 1000));
    return () => timers.forEach(clearTimeout);
  }, []);

  const allDone = checked >= CHECKLIST.length;

  return (
    <div className="w-56">
      {CHECKLIST.map((item, i) => {
        const done = i < checked;
        const Icon = item.icon;
        return (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, ease: CK_EASE, delay: item.delay }}
            className="flex items-center gap-3 border-b border-abyss-800/60 py-2 last:border-0"
          >
            <div
              className={`flex size-6 shrink-0 items-center justify-center rounded-md transition-colors duration-300 ${
                done ? "bg-shallow-500/15" : "bg-abyss-800/40"
              }`}
            >
              {done ? (
                <CheckCircle2Icon className="size-3.5 text-shallow-400" />
              ) : (
                <Icon className="size-3 text-abyss-500" />
              )}
            </div>
            <span className={`text-xs transition-colors duration-300 ${done ? "text-abyss-200" : "text-abyss-500"}`}>
              {item.label}
            </span>
          </motion.div>
        );
      })}
      {allDone && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2, ease: CK_EASE }}
          className="mt-3 text-center text-xs font-medium uppercase tracking-[0.15em] text-shallow-400"
        >
          Welcome aboard
        </motion.p>
      )}
    </div>
  );
}

// ── AppLoader ─────────────────────────────────────────────────────────────────

export function AppLoader({ label }: { label?: string }) {
  return (
    <motion.div
      role="status"
      aria-label={label ?? "Loading"}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-8 bg-abyss-950"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
    >
      <CompassRose />

      <div className="flex flex-col items-center gap-2">
        <span className="font-heading text-2xl font-bold tracking-[-0.02em] text-white" aria-hidden="true">
          Bowfast
        </span>
        {label ? (
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-abyss-300">{label}</span>
        ) : (
          <DepartureChecklist />
        )}
      </div>

      <div className="h-px w-48 overflow-hidden rounded-full bg-abyss-800">
        <div className="h-full w-full origin-left animate-[shimmer_1.8s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-abyss-400 to-transparent" />
      </div>
    </motion.div>
  );
}

// ── AppError ──────────────────────────────────────────────────────────────────

interface AppErrorProps {
  /** HTTP status code, e.g. 404 or 500 */
  status?: number;
  /** Short headline — defaults derived from status */
  title?: string;
  /** Longer explanation */
  message?: string;
  /** CTA action — defaults to window.history.back() */
  onRetry?: () => void;
  /** CTA label */
  retryLabel?: string;
}

function statusToKind(status?: number): ErrorKind {
  if (!status || status < 500) return "4xx";
  return "5xx";
}

const STATUS_DEFAULTS: Record<number, { title: string; message: string }> = {
  400: { title: "Bad request", message: "The request couldn't be understood. Check your input and try again." },
  401: { title: "Not authenticated", message: "You need to sign in before accessing this page." },
  403: { title: "Crew-only waters", message: "You don't have permission to access this page." },
  404: {
    title: "Uncharted waters",
    message: "We couldn't find what you were looking for. The position may have changed.",
  },
  429: {
    title: "Too many requests",
    message: "You're making requests too quickly. Take a breath and try again shortly.",
  },
  500: {
    title: "Something broke on deck",
    message: "An unexpected error occurred on our end. The crew has been notified.",
  },
  502: { title: "Gateway adrift", message: "The upstream service isn't responding. Try again in a moment." },
  503: {
    title: "Below deck for maintenance",
    message: "We're performing scheduled maintenance. We'll be back shortly.",
  },
};

export function AppError({ status = 404, title, message, onRetry, retryLabel = "Return to port" }: AppErrorProps) {
  const kind = statusToKind(status);
  const defaults = STATUS_DEFAULTS[status] ?? {
    title: kind === "5xx" ? "Something went wrong" : "Page not found",
    message: kind === "5xx" ? "An unexpected error occurred. Please try again." : "We couldn't find that page.",
  };

  const resolvedTitle = title ?? defaults.title;
  const resolvedMessage = message ?? defaults.message;
  const cfg = ERROR_CONFIG[kind];

  function handleRetry() {
    if (onRetry) {
      onRetry();
    } else {
      window.history.back();
    }
  }

  return (
    <div
      role="alert"
      aria-labelledby={ERROR_CANVAS_ID}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-8 bg-abyss-950"
    >
      <BrokenCompass kind={kind} />

      {/* Status code + title */}
      <div className="flex flex-col items-center gap-2 text-center">
        <span
          className="font-heading text-[4rem] font-bold leading-none tracking-[-0.04em]"
          style={{ color: cfg.color }}
          aria-hidden="true"
        >
          {status}
        </span>
        <span id={ERROR_CANVAS_ID} className="font-heading text-xl font-semibold tracking-[-0.01em] text-white">
          {resolvedTitle}
        </span>
        <p className="mt-1 max-w-sm text-sm leading-relaxed text-abyss-300">{resolvedMessage}</p>
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={handleRetry}
        className="rounded-lg border border-abyss-700 bg-abyss-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-abyss-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-abyss-400"
      >
        {retryLabel}
      </button>
    </div>
  );
}
